import { Navigation } from "@/components/Navigation";
import { Plus, Edit, Eye, Lightbulb } from "lucide-react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { useTranslation } from "react-i18next";
import { useQuery } from "@tanstack/react-query";
import { MealsTable } from "@/components/meals/MealsTable";
import { useState, useEffect } from "react";
import { useIsMobile } from "@/hooks/use-mobile";
import { diets } from "@/i18n/locales/diets";
import { WeightTable } from "@/components/weight/WeightTable";
import { WeightChart } from "@/components/charts/WeightChart";
import Prisma from "@prisma/client";
import { CaloriesChart } from "@/components/charts/CaloriesChart";

import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Card } from "@/components/ui/card";

const Dashboard = () => {
    const { t, i18n } = useTranslation();
    const { user, currentDietId, authFetch } = useAuth();
    const navigate = useNavigate();
    const [showConfirmDialog, setShowConfirmDialog] = useState(false);
    const [diet, setDiet] = useState<Prisma.Diet | null>(null);
    const isMobile = useIsMobile();
    const availableDiets = diets[i18n.language] || diets["en"];
    const { data: currentDiet = null } = useQuery({
        queryKey: ["diet", currentDietId],
        queryFn: async () => {
            const response = await authFetch("/api/diet");
            if (!response.ok) {
                throw new Error("Failed to fetch active diet");
            }
            const json = await response.json();
            return json.diet as Prisma.Diet;
        },
        enabled: !!user,
    });

    const { data: meals = [] } = useQuery({
        queryKey: ["meals", "today"],
        queryFn: async () => {
            const response = await authFetch("/api/meals/today");
            if (!response.ok) {
                throw new Error("Failed to fetch meals");
            }
            return response.json() as Promise<Prisma.Meal[]>;
        },
        enabled: !!currentDiet,
    });

    const { data: lastMonthCalories = [] } = useQuery({
        queryKey: ["meals", "lastMonth"],
        queryFn: async () => {
            const response = await authFetch("/api/meals/trend");
            if (!response.ok) {
                throw new Error("Failed to fetch meals");
            }
            return response.json();
        },
        enabled: true,
    });

    const { data: weightLog = [] } = useQuery({
        queryKey: ["weight", "lastMonth"],
        queryFn: async () => {
            const response = await authFetch("/api/weight/logs");
            if (!response.ok) {
                throw new Error("Failed to fetch weight");
            }
            const json = await response.json();
            return json.weightLogs;
        },
        enabled: true,
    });

    const [totalCalories, setTotalCalories] = useState<number>(0);
    const [availableCalories, setAvailableCalories] = useState<number>(0);

    useEffect(() => {
        const calories = meals.reduce((acc: number, meal) => acc + meal.calories, 0);
        setTotalCalories(calories);
        setAvailableCalories((currentDiet?.calories || 0) - calories);
    }, [currentDiet, meals]);

    const handleNewDiet = () => {
        setShowConfirmDialog(true);
    };

    const confirmNewDiet = () => {
        setShowConfirmDialog(false);
        navigate("/build-diet?new=true");
    };

    const handleEditDiet = () => {
        navigate("/build-diet?edit=true");
    };

    const suggestMeal = async () => {
        navigate("/suggest-meal");
    };

    const dietType = availableDiets.find((diet) => diet.id === currentDiet?.type);

    return (
        <div className="min-h-screen bg-gray-50">
            <Navigation />

            <div className="pt-24 pb-6 px-4 sm:px-6 lg:px-8">
                <div className="max-w-7xl mx-auto flex flex-col space-y-6">
                    <div className="flex justify-between items-center mb-8 flex-wrap md:flex-nowrap gap-y-3">
                        <h1 className={"text-3xl font-bold text-gray-900 sm:w-[100%]"}>{t("dashboard.title")}</h1>
                        {currentDiet ? (
                            <div className="flex gap-2 flex-wrap md:flex-nowrap gap-y-3">
                                {/* Visualizza dieta */}
                                <Button onClick={() => navigate("/view-diet")}>
                                    <Eye size={16} className="mr-2 hidden md:black" />
                                    {t("dashboard.diet.view")}
                                </Button>
                                {/* Modifica dieta */}
                                <Button onClick={handleEditDiet} variant="outline">
                                    <Edit size={16} className="mr-2 hidden md:black" />
                                    {t("dashboard.diet.edit")}
                                </Button>
                                {/* Nuova dieta */}
                                <Button onClick={handleNewDiet} variant="outline">
                                    <Plus size={16} className="mr-2 hidden md:black" />
                                    {t("dashboard.diet.new")}
                                </Button>
                            </div>
                        ) : (
                            <Button asChild>
                                {/* Crea nuova dieta se non esiste */}
                                <Link to="/build-diet?new=true">
                                    <Plus size={16} className="mr-2" />
                                    {t("dashboard.diet.create")}
                                </Link>
                            </Button>
                        )}
                    </div>

                    {currentDiet && (
                        <>
                            <Card className="p-6 space-y-4">
                                <span className="text-xl font-semibold mb-2">{t("dashboard.dietType") + ": " + dietType.name} </span> -
                                <span className="text-gray-600 mb-4"> {dietType.description}</span>
                            </Card>
                            <Card className="p-6 space-y-4">
                                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                    {isMobile ? null : (
                                        <div>
                                            <p className="text-sm text-gray-500">{t("buildDiet.preferences.bmr")}</p>
                                            <p className="text-lg font-medium">
                                                {currentDiet.bmr} {t("buildDiet.preferences.kcal")}
                                            </p>
                                        </div>
                                    )}

                                    <div>
                                        <p className="text-sm text-gray-500">{t("buildDiet.preferences.tdee")}</p>
                                        <p className="text-lg font-medium">
                                            {currentDiet.tdee} {t("buildDiet.preferences.kcal")}
                                        </p>
                                    </div>
                                    <div>
                                        <p className="text-sm text-gray-500">{t("buildDiet.preferences.targetCalories")}</p>
                                        <p className="text-lg font-medium">
                                            {currentDiet.calories} {t("buildDiet.preferences.kcal")}
                                        </p>
                                    </div>
                                </div>
                            </Card>

                            <Card className="space-y-4">
                                <MealsTable meals={meals} totalCalories={totalCalories} availableCalories={availableCalories} />
                                <div className="p-6 flex gap-2">
                                    <Button onClick={() => navigate("/add-meal")}>
                                        <Plus size={16} className="mr-2" />
                                        {t("dashboard.meals.addMeal")}
                                    </Button>

                                    {/* Pulsante per suggerire un pasto */}
                                    <Button onClick={suggestMeal} variant="outline">
                                        <Lightbulb size={16} className="mr-2" /> {/* Icona suggerimento */}
                                        {t("dashboard.meals.suggestMeal")}
                                    </Button>
                                </div>
                            </Card>
                        </>
                    )}

                    <Card>
                        <WeightTable weightLog={weightLog} />
                        <div className="p-6 flex gap-2">
                            <Button onClick={() => navigate("/add-weight")}>
                                <Plus size={16} className="mr-2" />
                                {t("dashboard.weight.addWeight")}
                            </Button>
                        </div>
                    </Card>

                    <Card>
                        <CaloriesChart data={lastMonthCalories} />
                    </Card>

                    <Card>
                        <WeightChart weightLog={weightLog.map((log) => ({ ...log, date: log.date.slice(0, 10) }))} />
                    </Card>
                </div>
            </div>

            {/* Dialogo di conferma per nuova dieta */}
            <AlertDialog open={showConfirmDialog} onOpenChange={setShowConfirmDialog}>
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle>{t("dashboard.confirmDialog.title")}</AlertDialogTitle>
                        <AlertDialogDescription>{t("dashboard.confirmDialog.description")}</AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel onClick={() => setShowConfirmDialog(false)}>{t("dashboard.confirmDialog.cancel")}</AlertDialogCancel>
                        <AlertDialogAction onClick={confirmNewDiet}>{t("dashboard.confirmDialog.confirm")}</AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </div>
    );
};

export default Dashboard;
